'use client';

import React from 'react';
import { cn } from '@/lib/utils';

interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'small' | 'muted' | 'lead';
  as?: keyof React.JSX.IntrinsicElements;
  weight?: 'normal' | 'medium' | 'semibold' | 'bold';
  align?: 'left' | 'center' | 'right';
  color?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'muted'
    | 'accent'
    | 'success'
    | 'warning'
    | 'error';
  truncate?: boolean;
}

const variantStyles = {
  h1: 'text-3xl sm:text-4xl font-bold tracking-tight',
  h2: 'text-2xl sm:text-3xl font-semibold tracking-tight',
  h3: 'text-xl sm:text-2xl font-semibold tracking-tight',
  h4: 'text-lg sm:text-xl font-semibold tracking-tight',
  p: 'text-base leading-7',
  small: 'text-sm leading-6',
  muted: 'text-sm text-muted-foreground leading-6',
  lead: 'text-lg sm:text-xl font-medium text-muted-foreground',
};

const weightStyles = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

const alignStyles = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const colorStyles = {
  default: 'text-foreground',
  primary: 'text-primary',
  secondary: 'text-secondary',
  muted: 'text-muted-foreground',
  accent: 'text-accent',
  success: 'text-green-600',
  warning: 'text-amber-600',
  error: 'text-red-600',
};

export const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  (
    {
      className,
      variant = 'p',
      as,
      weight,
      align,
      color = 'default',
      truncate = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Component =
      as ||
      (variant === 'h1'
        ? 'h1'
        : variant === 'h2'
          ? 'h2'
          : variant === 'h3'
            ? 'h3'
            : variant === 'h4'
              ? 'h4'
              : variant === 'small'
                ? 'small'
                : 'p');

    return React.createElement(
      Component,
      {
        ref,
        className: cn(
          variantStyles[variant],
          weight && weightStyles[weight],
          align && alignStyles[align],
          colorStyles[color],
          truncate && 'truncate',
          className,
        ),
        ...props,
      },
      children,
    );
  },
);

Typography.displayName = 'Typography';

// Convenience components
export const Heading1 = (props: Omit<TypographyProps, 'variant' | 'as'>) => (
  <Typography variant="h1" as="h1" {...props} />
);

export const Heading2 = (props: Omit<TypographyProps, 'variant' | 'as'>) => (
  <Typography variant="h2" as="h2" {...props} />
);

export const Heading3 = (props: Omit<TypographyProps, 'variant' | 'as'>) => (
  <Typography variant="h3" as="h3" {...props} />
);

export const Heading4 = (props: Omit<TypographyProps, 'variant' | 'as'>) => (
  <Typography variant="h4" as="h4" {...props} />
);

export const Paragraph = (props: Omit<TypographyProps, 'variant' | 'as'>) => (
  <Typography variant="p" as="p" {...props} />
);

export const SmallText = (props: Omit<TypographyProps, 'variant' | 'as'>) => (
  <Typography variant="small" as="small" {...props} />
);

export const MutedText = (props: Omit<TypographyProps, 'variant' | 'as'>) => (
  <Typography variant="muted" as="p" {...props} />
);

export const LeadText = (props: Omit<TypographyProps, 'variant' | 'as'>) => (
  <Typography variant="lead" as="p" {...props} />
);

export default Typography;
